import React, { useState } from 'react';
import type { Editor } from '@tiptap/react';
import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import Undo from '@mui/icons-material/Undo';
import Redo from '@mui/icons-material/Redo';
import FormatBold from '@mui/icons-material/FormatBold';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatUnderlined from '@mui/icons-material/FormatUnderlined';
import StrikethroughS from '@mui/icons-material/StrikethroughS';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslate } from 'react-admin';
import { LinkPromptDialog } from '../../../common/LinkPromptDialog';
import { getBlockMenuItems, type BlockMenuItem } from './blockMenuItems';
import { newBlockId } from './blockId';

interface ToolbarButtonProps {
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ onClick, isActive, disabled, title, children }) => (
    <Tooltip title={title}>
        <span>
            <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={onClick}
                disabled={disabled}
                aria-label={title}
                aria-pressed={isActive}
                sx={{
                    borderRadius: 'var(--oa-radius-sm, 6px)',
                    color: isActive ? 'var(--oa-primary)' : 'var(--oa-text-2)',
                    backgroundColor: isActive ? 'color-mix(in srgb, var(--oa-primary) 12%, transparent)' : 'transparent',
                }}
            >
                {children}
            </IconButton>
        </span>
    </Tooltip>
);

/** Position right after the top-level node that holds the selection end. */
export function positionAfterSelectedBlock(editor: Editor): number {
    const { $to } = editor.state.selection;
    // #526: with an atom block selected (image, quiz …) `$to` sits at depth 0 —
    // `$to.end() + 1` then pointed behind the document and the insert threw.
    return $to.depth === 0 ? $to.pos : $to.after(1);
}

/** Inserts a block after the current top-level node. */
export function insertBlockAfterSelection(editor: Editor, item: Pick<BlockMenuItem, 'nodeType' | 'attrs'>): void {
    editor
        .chain()
        .focus()
        .insertContentAt(positionAfterSelectedBlock(editor), { type: item.nodeType, attrs: { blockId: newBlockId(), ...item.attrs } })
        .run();
}

/**
 * Toolbar: undo/redo, inline marks, link, quick insert of the most common blocks.
 */
export const BlockEditorToolbar: React.FC<{ editor: Editor; onToggleLibrary: () => void; libraryOpen: boolean }> = ({ editor }) => {
    const translate = useTranslate();
    const [linkOpen, setLinkOpen] = useState(false);
    const quickInsert = getBlockMenuItems(translate).filter((i) => ['richtextBlock', 'headingBlock', 'quoteBlock', 'codeBlockNode', 'dividerBlock', 'imageBlock', 'calloutBlock'].includes(i.nodeType));

    const applyLink = (url: string | null) => {
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    return (
        <Box className="oa-block-toolbar" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.25, px: 1, py: 0.5 }}>
            <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title={translate('blockEditor.toolbar.undo')}>
                <Undo fontSize="small" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title={translate('blockEditor.toolbar.redo')}>
                <Redo fontSize="small" />
            </ToolbarButton>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title={translate('blockEditor.toolbar.bold')}>
                <FormatBold fontSize="small" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title={translate('blockEditor.toolbar.italic')}>
                <FormatItalic fontSize="small" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title={translate('blockEditor.toolbar.underline')}>
                <FormatUnderlined fontSize="small" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title={translate('blockEditor.toolbar.strike')}>
                <StrikethroughS fontSize="small" />
            </ToolbarButton>
            <ToolbarButton onClick={() => setLinkOpen(true)} isActive={editor.isActive('link')} title={translate('blockEditor.toolbar.link')}>
                <LinkIcon fontSize="small" />
            </ToolbarButton>
            <LinkPromptDialog open={linkOpen} initialUrl={editor.getAttributes('link').href || ''} onClose={() => setLinkOpen(false)} onSubmit={applyLink} />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {quickInsert.map((item) => {
                const Icon = item.icon;
                return (
                    <ToolbarButton key={item.key} onClick={() => insertBlockAfterSelection(editor, item)} title={item.label}>
                        <Icon fontSize="small" />
                    </ToolbarButton>
                );
            })}
        </Box>
    );
};
